import styled, { css, keyframes } from 'styled-components'
import colors from 'utils/styling/colors'

/** Eases a photo between full and half opacity while a generation runs. */
const pulse = keyframes`
  from { opacity: 1; }
  to { opacity: 0.5; }
`

type PulsingProps = {
  $pulsing?: boolean
}

/** Where the Enhance panel is drawn; each host picks one. */
export type EnhanceLayout = 'takeover' | 'modal'

type LayoutProps = {
  $layout: EnhanceLayout
}

const takeoverBox = css`
  min-height: 200px;
  padding: 24px;
  margin: 0 0 24px 0;
  border: 1px solid ${colors('gray4')};
  border-radius: 4px;
  background: ${colors('white')};
`

/**
 * The Enhance panel for the stages that show the source photo (disclosure,
 * enhancing, error, revert). Its children are always [thumbnail, copy,
 * actions] in that order; the layout decides how they sit:
 *
 * - `takeover` replaces the upload dropzone inside the case details modal
 *   (a modal is never opened over that modal). The thumbnail sits left and
 *   spans both rows; copy and actions stack beside it. Same footprint as
 *   the dropzone it replaces.
 * - `modal` is the centred column inside the editor's Enhance modal:
 *   thumbnail, copy, then full-width buttons.
 */
export const Panel = styled.div<LayoutProps>`
  width: 100%;
  box-sizing: border-box;

  ${({ $layout }) =>
    $layout === 'takeover'
      ? css`
          display: grid;
          grid-template-columns: auto 1fr;
          column-gap: 24px;
          row-gap: 12px;
          align-items: start;
          ${takeoverBox}
        `
      : css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        `}
`

/**
 * The Enhance panel for the stages built around the compare slider
 * (complete, compare): a column in both layouts. The modal layout centres
 * the heading the way the Figma frames draw it.
 */
export const StackPanel = styled.div<LayoutProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  ${({ $layout }) =>
    $layout === 'takeover'
      ? takeoverBox
      : css`
          gap: 24px;
        `}
`

/**
 * Holds the thumbnail so the sparkle badge can overhang its top-right
 * corner. In the takeover layout it spans both grid rows so the copy and
 * the buttons stack beside it.
 */
export const ThumbnailAnchor = styled.div<LayoutProps>`
  position: relative;
  flex-shrink: 0;

  ${({ $layout }) =>
    $layout === 'takeover'
      ? css`
          grid-row: 1 / span 2;
        `
      : css`
          margin-top: 10px;
        `}
`

/**
 * Thumbnail of the image being enhanced. The modal layout draws it with the
 * brand outline and glow from the Figma frames; the takeover keeps the plain
 * grey border of the surrounding grid.
 */
export const PanelThumbnail = styled.img<PulsingProps & LayoutProps>`
  display: block;
  object-fit: cover;
  border-radius: 4px;

  ${({ $layout }) =>
    $layout === 'takeover'
      ? css`
          width: 120px;
          height: 120px;
          border: 1px solid ${colors('gray4')};
        `
      : css`
          width: 125px;
          height: 125px;
          border: 1px solid ${colors('brand')};
          box-shadow: 0 0 3px 2px ${colors('brand', 40)};
        `}

  ${({ $pulsing }) =>
    $pulsing &&
    css`
      animation: ${pulse} 1s ease-in-out infinite alternate;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
        opacity: 0.75;
      }
    `}
`

/** The sparkle badge overhanging the thumbnail's top-right corner (modal). */
export const SparkleBadge = styled.span`
  position: absolute;
  top: -10px;
  right: -15px;
  display: flex;
  padding: 2px;
  border-radius: 4px;
  background: ${colors('gray5')};
`

type PanelCopyProps = LayoutProps & {
  /** The disclosure's price sits to the right of the title in the modal. */
  $priced?: boolean
}

/**
 * The copy block of a framed stage. In the takeover it is a plain column.
 * The modal disclosure lays its [title, price, body, notice] children on a
 * two-column grid so the price sits on the title's right, as designed,
 * without reordering the DOM; every child after the price stays in the
 * first column.
 */
export const PanelCopy = styled.div<PanelCopyProps>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  ${({ $layout, $priced }) =>
    $layout === 'modal' &&
    css`
      width: 100%;
      align-items: center;

      ${$priced &&
      css`
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        column-gap: 16px;
        row-gap: 8px;
        align-items: start;

        > :nth-child(n + 3) {
          grid-column: 1;
        }
      `}
    `}
`

type ActionsProps = LayoutProps & {
  /** The compact error dialog: Cancel left, Try again right. */
  $spread?: boolean
}

/**
 * The buttons of a stage. Takeover: a row under the copy. Modal: stacked,
 * full-width buttons with centred labels, or the `$spread` row of the
 * compact error dialog.
 */
export const Actions = styled.div<ActionsProps>`
  display: flex;
  gap: 12px;

  ${({ $layout, $spread }) =>
    $layout === 'takeover'
      ? css`
          grid-column: 2;
        `
      : $spread
        ? css`
            width: 100%;
            justify-content: space-between;

            > button {
              width: 100px;
              justify-content: center;
            }
          `
        : css`
            flex-direction: column;
            gap: 8px;
            width: 100%;
            margin-top: 8px;

            > button {
              width: 100%;
              justify-content: center;
            }
          `}
`

const slide = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(400%); }
`

/**
 * Indeterminate progress bar for the modal's enhancing stage. The backend
 * reports queued/processing but no percentage, so the track just travels;
 * people who asked for reduced motion get a steady quarter fill.
 */
export const ProgressBar = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
  margin-top: 8px;
  border-radius: 9999px;
  background: ${colors('gray5')};
  overflow: hidden;
`

/** The moving fill inside `ProgressBar`. */
export const ProgressTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 25%;
  height: 100%;
  border-radius: 9999px;
  background: ${colors('brand')};
  animation: ${slide} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

/** Which of the designed modal widths a stage uses. */
export type EnhanceModalSize = 'regular' | 'wide' | 'compact'

const MODAL_WIDTH_PX: Record<EnhanceModalSize, number> = {
  regular: 650,
  wide: 1064,
  compact: 400,
}

/**
 * The Enhance modal's content box, centred over the editor by the shared
 * fullscreen modal. Three widths from the Figma frames: the regular 650px
 * dialog, the wide one whose compare slider spans 1000px, and the compact
 * 400px error dialog. It never exceeds the viewport and scrolls when a tall
 * photo makes the slider taller than the window.
 */
export const ModalBody = styled.div<{ $size: EnhanceModalSize }>`
  position: relative;
  box-sizing: border-box;
  width: ${({ $size }) => MODAL_WIDTH_PX[$size]}px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: ${({ $size }) => ($size === 'compact' ? '24px' : '32px')};
  outline: none;
`

/** Row of small thumbnails for previous enhancements. */
export const PreviousRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

type PreviousThumbProps = {
  $selected?: boolean
}

/** One previous enhancement thumbnail; the selected one is outlined. */
export const PreviousThumb = styled.button<PreviousThumbProps>`
  width: 56px;
  height: 56px;
  padding: 0;
  border: 2px solid
    ${({ $selected }) => ($selected ? colors('brand') : colors('gray4'))};
  border-radius: 4px;
  overflow: hidden;
  background: ${colors('gray5')};
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`
